import { useEffect, useMemo } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { ErrorBoundary } from 'react-error-boundary'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'

import AppContent from '../AppContent'
import AppErrorFallback from '../../components/errors/AppErrorFallback'
import {
  getPreferredColorMode,
  selectColorMode,
  setColorMode,
} from '../../features/preferences'
import { store, userSessionCleared } from '../../store'
import { subscribeAuthSession } from '../../services/authSession'
import { clearActivityTimerSnapshot } from '../../services/activityTimerPersistence'
import { startPosthogLifecycle } from '../../services/posthogLifecycle'
import { trackClientError } from '../../services/posthogAnalytics'
import GlobalStyle from '../../styles/GlobalStyle'
import { createMuiTheme } from '../../styles/muiTheme'
import { createAppTheme } from '../../styles/theme'

function saveColorMode(mode) {
  document.documentElement.dataset.colorMode = mode
  document.documentElement.style.colorScheme = mode
  document.querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', mode === 'dark' ? '#111722' : '#FAFBFD')

  try {
    window.localStorage.setItem('akive-color-mode', mode)
  } catch {
    // 저장소 접근이 제한돼도 현재 탭의 테마 전환은 유지한다.
  }
}

function handleRenderError() {
  trackClientError({
    errorCode: 'REACT_RENDER_ERROR',
    source: 'error_boundary',
  })
}

function ThemedApp() {
  const dispatch = useDispatch()
  const mode = useSelector(selectColorMode)
  const theme = useMemo(() => createAppTheme(mode), [mode])
  const muiTheme = useMemo(() => createMuiTheme(mode), [mode])

  useEffect(() => {
    dispatch(setColorMode(getPreferredColorMode()))
  }, [dispatch])

  useEffect(() => subscribeAuthSession((change) => {
    if (change?.action !== 'cleared') return
    clearActivityTimerSnapshot(change.previousSession, { force: true })
    dispatch(userSessionCleared())
  }), [dispatch])

  useEffect(() => startPosthogLifecycle(), [])

  useEffect(() => {
    saveColorMode(mode)
  }, [mode])

  return (
    <StyledThemeProvider theme={theme}>
      <MuiThemeProvider theme={muiTheme}>
        <GlobalStyle />
        <ErrorBoundary
          FallbackComponent={AppErrorFallback}
          onError={handleRenderError}
        >
          <AppContent />
        </ErrorBoundary>
      </MuiThemeProvider>
    </StyledThemeProvider>
  )
}

export default function AppProviders() {
  return (
    <Provider store={store}>
      <ThemedApp />
    </Provider>
  )
}
